import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Lock, User, UserPlus, Sparkles, Shield, FileText, ArrowRight, Building2, Zap, Globe, Cpu } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import Banned from './Banned';

const API_URL = process.env.REACT_APP_BACKEND_URL 
  ? `${process.env.REACT_APP_BACKEND_URL}/api`
  : 'https://nova-network-2-0-backend.onrender.com/api';

const Login = () => {
  const [isRegister, setIsRegister] = useState(false);
  const [loginData, setLoginData] = useState({ username: '', password: '' });
  const [registerData, setRegisterData] = useState({ username: '', password: '', nome: '', cognome: '' });
  const [isLoading, setIsLoading] = useState(false);
  const { login, banInfo } = useAuth();
  const navigate = useNavigate();

  if (banInfo) {
    return <Banned banInfo={banInfo} />;
  }

  const handleLogin = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      await login(loginData.username, loginData.password);
      toast.success('Benvenuto!');
      navigate('/dashboard');
    } catch (error) {
      if (error.message === 'BANNED') {
        return;
      }
      toast.error(error.response?.data?.detail || 'Credenziali non valide');
    } finally {
      setIsLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const response = await axios.post(`${API_URL}/auth/register`, registerData);
      const { access_token } = response.data;
      localStorage.setItem('token', access_token);
      axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
      toast.success('Registrazione completata!');
      window.location.href = '/dashboard';
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nella registrazione');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex bg-white relative overflow-hidden">
      {/* Animated Background Elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {/* Grid Pattern */}
        <div className="absolute inset-0 opacity-[0.03]" style={{
          backgroundImage: `linear-gradient(rgba(0,0,0,0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(0,0,0,0.1) 1px, transparent 1px)`,
          backgroundSize: '60px 60px'
        }} />
        
        {/* Floating Orbs */}
        <div className="absolute top-20 left-20 w-72 h-72 bg-gradient-to-br from-cyan-100 to-blue-100 rounded-full blur-3xl opacity-60 animate-pulse" />
        <div className="absolute bottom-20 right-20 w-96 h-96 bg-gradient-to-br from-violet-100 to-purple-100 rounded-full blur-3xl opacity-50" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-gradient-to-br from-slate-50 to-gray-100 rounded-full blur-3xl opacity-70" />
        
        {/* Decorative Lines */}
        <div className="absolute top-0 left-1/4 w-px h-full bg-gradient-to-b from-transparent via-slate-200 to-transparent" />
        <div className="absolute top-0 right-1/4 w-px h-full bg-gradient-to-b from-transparent via-slate-200 to-transparent" />
      </div>

      {/* Left Panel - Form */}
      <div className="w-full lg:w-1/2 flex flex-col justify-center px-8 lg:px-20 py-12 relative z-10">
        {/* Logo */}
        <div className="mb-12">
          <div className="flex items-center gap-4">
            <div className="relative group">
              <div className="absolute -inset-1 bg-gradient-to-r from-cyan-500 via-blue-500 to-violet-500 rounded-2xl blur opacity-30 group-hover:opacity-50 transition duration-500" />
              <img 
                src="/nova-logo.png" 
                alt="Nova Roleplay" 
                className="relative w-16 h-16 rounded-2xl object-cover shadow-xl"
              />
            </div>
            <div>
              <h1 className="text-2xl font-bold bg-gradient-to-r from-slate-900 via-slate-700 to-slate-900 bg-clip-text text-transparent tracking-tight">
                Nova Roleplay
              </h1>
              <div className="flex items-center gap-2 mt-1">
                <div className="w-2 h-2 bg-emerald-500 rounded-full animate-pulse" />
                <p className="text-sm text-slate-500 font-medium">Sistema Operativo</p>
              </div>
            </div>
          </div>
        </div>

        {/* Form Container */}
        <div className="max-w-md">
          <div className="mb-8">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-slate-100 text-slate-600 text-xs font-medium mb-4">
              <Cpu className="w-3 h-3" />
              {isRegister ? 'NUOVO UTENTE' : 'ACCESSO SISTEMA'}
            </div>
            <h2 className="text-4xl font-bold text-slate-900 mb-3 tracking-tight">
              {isRegister ? 'Crea Account' : 'Bentornato'}
            </h2>
            <p className="text-slate-500 text-lg">
              {isRegister 
                ? 'Inizia la tua esperienza nel sistema' 
                : 'Accedi per continuare'}
            </p>
          </div>

          {!isRegister ? (
            <form onSubmit={handleLogin} className="space-y-6">
              <div className="space-y-2">
                <label className="text-sm font-semibold text-slate-700 uppercase tracking-wider">Username</label>
                <div className="relative group">
                  <div className="absolute -inset-0.5 bg-gradient-to-r from-slate-200 to-slate-300 rounded-xl opacity-0 group-focus-within:opacity-100 transition duration-300 blur" />
                  <div className="relative">
                    <User className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                    <Input
                      data-testid="login-username"
                      type="text"
                      value={loginData.username}
                      onChange={(e) => setLoginData({ ...loginData, username: e.target.value })}
                      placeholder="Il tuo username"
                      className="pl-12 h-14 bg-white border-2 border-slate-200 rounded-xl text-slate-800 placeholder:text-slate-400 focus:border-slate-900 focus:ring-0 transition-all duration-300"
                      required
                    />
                  </div>
                </div>
              </div>

              <div className="space-y-2">
                <label className="text-sm font-semibold text-slate-700 uppercase tracking-wider">Password</label>
                <div className="relative group">
                  <div className="absolute -inset-0.5 bg-gradient-to-r from-slate-200 to-slate-300 rounded-xl opacity-0 group-focus-within:opacity-100 transition duration-300 blur" />
                  <div className="relative">
                    <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                    <Input
                      data-testid="login-password"
                      type="password"
                      value={loginData.password}
                      onChange={(e) => setLoginData({ ...loginData, password: e.target.value })}
                      placeholder="••••••••"
                      className="pl-12 h-14 bg-white border-2 border-slate-200 rounded-xl text-slate-800 placeholder:text-slate-400 focus:border-slate-900 focus:ring-0 transition-all duration-300"
                      required
                    />
                  </div>
                </div>
              </div>

              <Button
                type="submit"
                data-testid="login-submit"
                className="w-full h-14 bg-slate-900 hover:bg-slate-800 text-white font-semibold rounded-xl shadow-xl shadow-slate-900/20 transition-all duration-300 hover:shadow-slate-900/30 hover:scale-[1.02] group"
                disabled={isLoading}
              >
                {isLoading ? (
                  <div className="flex items-center gap-2">
                    <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    Accesso in corso...
                  </div>
                ) : (
                  <div className="flex items-center gap-2">
                    Accedi al Sistema
                    <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                  </div>
                )}
              </Button>
            </form>
          ) : (
            <form onSubmit={handleRegister} className="space-y-5">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <label className="text-sm font-semibold text-slate-700 uppercase tracking-wider">Nome</label>
                  <Input
                    data-testid="register-nome"
                    type="text"
                    value={registerData.nome}
                    onChange={(e) => setRegisterData({ ...registerData, nome: e.target.value })}
                    placeholder="Mario"
                    className="h-12 bg-white border-2 border-slate-200 rounded-xl focus:border-slate-900 focus:ring-0"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-semibold text-slate-700 uppercase tracking-wider">Cognome</label>
                  <Input
                    data-testid="register-cognome"
                    type="text"
                    value={registerData.cognome}
                    onChange={(e) => setRegisterData({ ...registerData, cognome: e.target.value })}
                    placeholder="Rossi"
                    className="h-12 bg-white border-2 border-slate-200 rounded-xl focus:border-slate-900 focus:ring-0"
                    required
                  />
                </div>
              </div>

              <div className="space-y-2">
                <label className="text-sm font-semibold text-slate-700 uppercase tracking-wider">Username</label>
                <div className="relative">
                  <User className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                  <Input
                    data-testid="register-username"
                    type="text"
                    value={registerData.username}
                    onChange={(e) => setRegisterData({ ...registerData, username: e.target.value })}
                    placeholder="Scegli un username"
                    className="pl-12 h-12 bg-white border-2 border-slate-200 rounded-xl focus:border-slate-900 focus:ring-0"
                    required
                  />
                </div>
              </div>

              <div className="space-y-2">
                <label className="text-sm font-semibold text-slate-700 uppercase tracking-wider">Password</label>
                <div className="relative">
                  <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                  <Input
                    data-testid="register-password"
                    type="password"
                    value={registerData.password}
                    onChange={(e) => setRegisterData({ ...registerData, password: e.target.value })}
                    placeholder="Password sicura"
                    className="pl-12 h-12 bg-white border-2 border-slate-200 rounded-xl focus:border-slate-900 focus:ring-0"
                    required
                  />
                </div>
              </div>

              <Button
                type="submit"
                data-testid="register-submit"
                className="w-full h-14 bg-slate-900 hover:bg-slate-800 text-white font-semibold rounded-xl shadow-xl shadow-slate-900/20"
                disabled={isLoading}
              >
                <UserPlus className="w-5 h-5 mr-2" />
                {isLoading ? 'Creazione...' : 'Crea Account'}
              </Button>
            </form>
          )}

          {/* Toggle Login/Register */}
          <div className="text-center mt-8">
            <button
              type="button"
              onClick={() => setIsRegister(!isRegister)}
              className="text-slate-500 hover:text-slate-900 transition-colors text-sm font-medium"
            >
              {isRegister ? (
                <>Hai già un account? <span className="text-slate-900 font-bold underline underline-offset-4">Accedi</span></>
              ) : (
                <>Non hai un account? <span className="text-slate-900 font-bold underline underline-offset-4">Registrati</span></>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Right Panel - Hero */}
      <div className="hidden lg:flex flex-1 relative overflow-hidden bg-gradient-to-br from-slate-50 via-white to-slate-100">
        {/* Geometric Background */}
        <div className="absolute inset-0">
          <div className="absolute inset-0 opacity-[0.02]" style={{
            backgroundImage: `radial-gradient(circle at 1px 1px, black 1px, transparent 0)`,
            backgroundSize: '40px 40px'
          }} />
        </div>
        
        {/* Floating Elements */}
        <div className="absolute top-1/4 right-1/4 w-64 h-64 border border-slate-200 rounded-full opacity-50" />
        <div className="absolute bottom-1/3 left-1/3 w-48 h-48 border border-slate-200 rounded-full opacity-30" />
        <div className="absolute top-1/2 right-1/3 w-32 h-32 bg-gradient-to-br from-cyan-50 to-blue-50 rounded-full blur-xl" />
        
        {/* Content */}
        <div className="relative z-10 flex flex-col justify-center items-center px-16 text-center">
          {/* Logo */}
          <div className="relative mb-10">
            <div className="absolute -inset-4 bg-gradient-to-r from-cyan-100 via-blue-100 to-violet-100 rounded-[40px] blur-xl opacity-60" />
            <img 
              src="/nova-logo.png" 
              alt="Nova Roleplay" 
              className="relative w-28 h-28 rounded-3xl object-cover shadow-2xl"
            />
          </div>
          
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-slate-900 text-white text-sm font-medium mb-6 shadow-lg">
            <Sparkles className="w-4 h-4" />
            Sistema Gestionale v2.0
          </div>
          
          <h2 className="text-5xl font-bold text-slate-900 mb-4 leading-tight tracking-tight">
            Nova Roleplay
          </h2>
          
          <p className="text-xl text-slate-500 mb-12 max-w-md leading-relaxed">
            La piattaforma completa per la gestione del tuo personaggio e dei servizi cittadini.
          </p>

          {/* Features Grid */}
          <div className="grid grid-cols-3 gap-6 w-full max-w-md">
            <div className="group p-5 bg-white rounded-2xl shadow-lg shadow-slate-200/50 hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
              <div className="w-12 h-12 bg-gradient-to-br from-cyan-50 to-cyan-100 rounded-xl flex items-center justify-center mb-3 mx-auto group-hover:scale-110 transition-transform">
                <Globe className="w-6 h-6 text-cyan-600" />
              </div>
              <p className="text-sm font-semibold text-slate-700">24+</p>
              <p className="text-xs text-slate-500">Aziende</p>
            </div>
            <div className="group p-5 bg-white rounded-2xl shadow-lg shadow-slate-200/50 hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
              <div className="w-12 h-12 bg-gradient-to-br from-violet-50 to-violet-100 rounded-xl flex items-center justify-center mb-3 mx-auto group-hover:scale-110 transition-transform">
                <Zap className="w-6 h-6 text-violet-600" />
              </div>
              <p className="text-sm font-semibold text-slate-700">100+</p>
              <p className="text-xs text-slate-500">Cittadini</p>
            </div>
            <div className="group p-5 bg-white rounded-2xl shadow-lg shadow-slate-200/50 hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
              <div className="w-12 h-12 bg-gradient-to-br from-emerald-50 to-emerald-100 rounded-xl flex items-center justify-center mb-3 mx-auto group-hover:scale-110 transition-transform">
                <Shield className="w-6 h-6 text-emerald-600" />
              </div>
              <p className="text-sm font-semibold text-slate-700">Sicuro</p>
              <p className="text-xs text-slate-500">Sistema</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
